import { SignedIn, SignedOut, useUser } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AdminRoute, RoleHome } from "./components/AdminRoute";
import { FloatingHelpButton } from "./components/FloatingHelpButton";
import { ServiceStatusBanner } from "./components/ServiceStatusBanner";
import { UserProfileProvider } from "./contexts/UserProfileContext";
import { DashboardLayout } from "./layouts/DashboardLayout";
import AccountPage from "./pages/AccountPage";
import AdminAssessmentFixtureDetailPage from "./pages/admin/AdminAssessmentFixtureDetailPage";
import AdminAssessmentFixtureEvalPage from "./pages/admin/AdminAssessmentFixtureEvalPage";
import AdminAssessmentFixturesPage from "./pages/admin/AdminAssessmentFixturesPage";
import AdminAssessmentRunDetailPage from "./pages/admin/AdminAssessmentRunDetailPage";
import AdminAssignmentsPage from "./pages/admin/AdminAssignmentsPage";
import AdminCertificatesPage from "./pages/admin/AdminCertificatesPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminEmailTemplatesPage from "./pages/admin/AdminEmailTemplatesPage";
import { AdminFaqsPage } from "./pages/admin/AdminFaqsPage";
import { AdminPeoplePage } from "./pages/admin/AdminPeoplePage";
import AdminProgrammesPage from "./pages/admin/AdminProgrammesPage";
import AdminReviewDetailPage from "./pages/admin/AdminReviewDetailPage";
import AdminReviewsPage from "./pages/admin/AdminReviewsPage";
import { AdminSourcesPage } from "./pages/admin/AdminSourcesPage";
import AdminSupportPage from "./pages/admin/AdminSupportPage";
import AdminTopicsPage from "./pages/admin/AdminTopicsPage";
import { AdminUnlockScreenPage } from "./pages/admin/AdminUnlockScreenPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminWebsiteFormsPage from "./pages/admin/AdminWebsiteFormsPage";
import ParticipantDetailPage from "./pages/admin/ParticipantDetailPage";
import ProgrammeUsersPage from "./pages/admin/ProgrammeUsersPage";
import { SeedEventsPage } from "./pages/admin/SeedEventsPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import GuestCheckoutPage, { PENDING_JOIN_KEY } from "./pages/GuestCheckoutPage";
import HowTheProgrammeWorksPage from "./pages/HowTheProgrammeWorksPage";
import HowToGetStartedPage from "./pages/HowToGetStartedPage";
import MarkingGuidePage from "./pages/MarkingGuidePage";
import MyProgrammesPage from "./pages/MyProgrammesPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import ProgramRegistrationPage from "./pages/ProgramRegistrationPage";
import RedeemPage, { PENDING_CODE_KEY } from "./pages/RedeemPage";
import ReviewPage from "./pages/ReviewPage";
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";
import SSOCallbackPage from "./pages/SSOCallbackPage";
import { readPendingFreeJoin, StartSignedIn, StartSignedOut } from "./pages/StartPage";
import SupportPage from "./pages/SupportPage";
import TimelinePage from "./pages/TimelinePage";
import UploadPage from "./pages/UploadPage";
import { api } from "./services/api";
import "./App.css";

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top when pathname changes
    if (pathname) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
}

// After a guest signs up, Clerk redirects them to "/". If they have a pending
// enrollment code (stored before signup), send them to /redeem to claim it.
function PendingRedeemRedirect() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (pathname === "/redeem") return;
    if (sessionStorage.getItem(PENDING_CODE_KEY)) {
      navigate("/redeem", { replace: true });
    }
  }, [pathname, navigate]);

  return null;
}

// After a guest picks a free cohort on /buy and signs up, Clerk lands them on
// "/". Forward the stashed cohort to /register, which auto-joins free cohorts
// via ?programme_id=... (see GuestCheckoutPage). A free join started on /start
// by someone who already had an account is finished back on /start once they
// sign in (KC-504).
function PendingJoinRedirect() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (pathname === "/start") return;
    const freeJoin = readPendingFreeJoin();
    if (freeJoin) {
      navigate(`/start?programme_id=${encodeURIComponent(freeJoin.programme_id)}`, {
        replace: true,
      });
      return;
    }
    if (pathname === "/register") return;
    const programmeId = sessionStorage.getItem(PENDING_JOIN_KEY);
    if (programmeId) {
      sessionStorage.removeItem(PENDING_JOIN_KEY);
      navigate(`/register?programme_id=${encodeURIComponent(programmeId)}`, { replace: true });
    }
  }, [pathname, navigate]);

  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <Navigate to="/sign-in" replace />
      </SignedOut>
    </>
  );
}

function SentryUserSync() {
  const { isLoaded, isSignedIn, user } = useUser();
  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn && user) {
      Sentry.setUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        username: user.fullName || user.username || undefined,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [isLoaded, isSignedIn, user]);
  return null;
}

// The public /buy link (guest checkout) is also shared with signed-in users.
// When signed in there is no guest flow, so forward them to the in-app
// registration page, preserving ?programme_id=... which it reads to auto-start
// checkout. Without this, /buy matched no signed-in route and rendered blank.
// /buy is paid-only, so a freemium Cohort goes to /start instead (KC-504).
function BuyRedirect() {
  const { search } = useLocation();
  const programmeId = new URLSearchParams(search).get("programme_id");
  const [target, setTarget] = useState<string | null>(programmeId ? null : "/register");

  useEffect(() => {
    if (!programmeId) return;
    let cancelled = false;
    api.getProgrammeById(programmeId).then((programme) => {
      if (!cancelled) setTarget(programme?.is_freemium ? "/start" : "/register");
    });
    return () => {
      cancelled = true;
    };
  }, [programmeId]);

  return target ? <Navigate to={`${target}${search}`} replace /> : null;
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
      <ServiceStatusBanner />
      <SentryUserSync />
      <BrowserRouter>
        <ScrollToTop />
        <SignedOut>
          <Routes>
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/sso-callback" element={<SSOCallbackPage />} />
            <Route path="/redeem" element={<RedeemPage />} />
            <Route path="/buy" element={<GuestCheckoutPage />} />
            <Route path="/start" element={<StartSignedOut />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="*" element={<Navigate to="/sign-in" replace />} />
          </Routes>
        </SignedOut>
        <SignedIn>
          <UserProfileProvider>
            <PendingRedeemRedirect />
            <PendingJoinRedirect />
            <Routes>
              <Route path="/redeem" element={<RedeemPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="/buy" element={<BuyRedirect />} />
              <Route path="/start" element={<StartSignedIn />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <RoleHome />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/submissions/:assignmentId"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <UploadPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <Navigate to="/admin/dashboard" replace />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminDashboardPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/programmes"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminProgrammesPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/assignments"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminAssignmentsPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/topics"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminTopicsPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/sources"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminSourcesPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/unlock-screen"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminUnlockScreenPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/faqs"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminFaqsPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/people"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminPeoplePage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/seed-events"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <SeedEventsPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminUsersPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <ParticipantDetailPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/website-forms"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminWebsiteFormsPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/support"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminSupportPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/email-templates"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminEmailTemplatesPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/certificates"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminCertificatesPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/programmes/:programmeId/users"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <ProgrammeUsersPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reviews"
                element={
                  <ProtectedRoute>
                    <AdminRoute allowedRoles={["admin", "assessor"]}>
                      <AdminReviewsPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reviews/:reviewId"
                element={
                  <ProtectedRoute>
                    <AdminRoute allowedRoles={["admin", "assessor"]}>
                      <AdminReviewDetailPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/assessment-fixtures"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminAssessmentFixturesPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/assessment-fixtures/runs/:runId"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminAssessmentRunDetailPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/assessment-fixtures/:fixtureId/eval"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminAssessmentFixtureEvalPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/assessment-fixtures/:fixtureId"
                element={
                  <ProtectedRoute>
                    <AdminRoute>
                      <AdminAssessmentFixtureDetailPage />
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/timeline"
                element={
                  <ProtectedRoute>
                    <TimelinePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-programmes"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <MyProgrammesPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reviews/:reviewId"
                element={
                  <ProtectedRoute>
                    <ReviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <ProtectedRoute>
                    <ProgramRegistrationPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/payment" element={<Navigate to="/account" replace />} />
              <Route
                path="/payment/success"
                element={
                  <ProtectedRoute>
                    <PaymentSuccessPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment/cancel"
                element={
                  <ProtectedRoute>
                    <PaymentCancelPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/how-the-programme-works"
                element={
                  <ProtectedRoute>
                    <HowTheProgrammeWorksPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/how-to-get-started"
                element={
                  <ProtectedRoute>
                    <HowToGetStartedPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/marking-guide"
                element={
                  <ProtectedRoute>
                    <MarkingGuidePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <SupportPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/sign-in" element={<Navigate to="/" replace />} />
              <Route path="/sign-up" element={<Navigate to="/" replace />} />
              <Route path="/forgot-password" element={<Navigate to="/" replace />} />
              <Route path="/sso-callback" element={<Navigate to="/" replace />} />
              {/* Any other unmatched route for a signed-in user goes to the
                  dashboard instead of rendering a blank page. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <FloatingHelpButton />
          </UserProfileProvider>
        </SignedIn>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

export default App;
